import React, { useState, useEffect, useMemo } from 'react';
import { Merge, Copy, Download, Shuffle, Eye, Settings, Hash, Zap, Code, List } from 'lucide-react';

interface JoinMethod {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface JoinSettings {
  trimItems: boolean;
  removeEmpty: boolean;
  removeDuplicates: boolean;
  ignoreCase: boolean;
  sortItems: boolean;
  reverseSort: boolean;
  addQuotes: boolean;
  escapeChars: boolean;
  addNumbering: boolean;
  addPrefix: string;
  addSuffix: string;
  wrapItems: boolean;
  wrapStart: string;
  wrapEnd: string;
  minLength: number;
  maxLength: number;
  limitCount: boolean;
  maxCount: number;
}

interface JoinResult {
  inputItems: string[];
  finalItems: string[];
  separator: string;
  output: string;
  stats: JoinStatistics;
}

interface JoinStatistics {
  originalCount: number;
  finalCount: number;
  emptyRemoved: number;
  duplicatesRemoved: number;
  outputLength: number;
  avgItemLength: number;
  longestItem: string;
  shortestItem: string;
  uniqueCount: number;
}

const StringJoinerTool: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [joinSeparator, setJoinSeparator] = useState(', ');
  const [selectedJoinMethod, setSelectedJoinMethod] = useState('basic');
  const [outputText, setOutputText] = useState('');
  const [copyMessage, setCopyMessage] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);

  const [settings, setSettings] = useState<JoinSettings>({
    trimItems: true,
    removeEmpty: false,
    removeDuplicates: false,
    ignoreCase: true,
    sortItems: false,
    reverseSort: false,
    addQuotes: false,
    escapeChars: false,
    addNumbering: false,
    addPrefix: '',
    addSuffix: '',
    wrapItems: false,
    wrapStart: '',
    wrapEnd: '',
    minLength: 0,
    maxLength: 1000,
    limitCount: false,
    maxCount: 100,
  });

  const joinMethods: JoinMethod[] = [
    {
      id: 'basic',
      name: 'Basic Join',
      description: 'Simple joining with separator',
      icon: <Merge className="w-4 h-4" />,
      color: 'blue'
    },
    {
      id: 'comma',
      name: 'Comma Separated',
      description: 'Standard comma-separated format',
      icon: <Hash className="w-4 h-4" />,
      color: 'green'
    },
    {
      id: 'array',
      name: 'Array Syntax',
      description: 'Programming array format',
      icon: <Code className="w-4 h-4" />,
      color: 'purple'
    },
    {
      id: 'bulletList',
      name: 'Bullet List',
      description: 'Bulleted list format',
      icon: <List className="w-4 h-4" />,
      color: 'red'
    },
    {
      id: 'numberedList',
      name: 'Numbered List',
      description: 'Sequential numbered list',
      icon: <Hash className="w-4 h-4" />,
      color: 'yellow'
    },
    {
      id: 'htmlList',
      name: 'HTML List',
      description: 'HTML unordered list',
      icon: <Code className="w-4 h-4" />,
      color: 'indigo'
    },
    {
      id: 'markdownList',
      name: 'Markdown List',
      description: 'Markdown format list',
      icon: <List className="w-4 h-4" />,
      color: 'pink'
    },
    {
      id: 'sqlValues',
      name: 'SQL Values',
      description: 'SQL VALUES clause format',
      icon: <Code className="w-4 h-4" />,
      color: 'orange'
    },
    {
      id: 'jsonArray',
      name: 'JSON Array',
      description: 'Valid JSON array format',
      icon: <Code className="w-4 h-4" />,
      color: 'teal'
    },
    {
      id: 'xmlList',
      name: 'XML List',
      description: 'XML list structure',
      icon: <Code className="w-4 h-4" />,
      color: 'cyan'
    },
    {
      id: 'naturlaText',
      name: 'Natural Text',
      description: 'Human-readable sentence',
      icon: <Zap className="w-4 h-4" />,
      color: 'emerald'
    },
    {
      id: 'customFormat',
      name: 'Custom Format',
      description: 'User-defined template',
      icon: <Settings className="w-4 h-4" />,
      color: 'violet'
    }
  ];

  const executeJoin = (text: string, separator: string, method: string, options: JoinSettings): JoinResult => {
    if (!text.trim()) {
      return {
        inputItems: [],
        finalItems: [],
        separator,
        output: '',
        stats: {
          originalCount: 0,
          finalCount: 0,
          emptyRemoved: 0,
          duplicatesRemoved: 0,
          outputLength: 0,
          avgItemLength: 0,
          longestItem: '',
          shortestItem: '',
          uniqueCount: 0
        }
      };
    }

    // Split input into items
    const inputItems = text.split('\n').map(item => item.replace(/\r$/, ''));
    let processedItems = [...inputItems];

    // Apply preprocessing
    processedItems = preprocessItems(processedItems, options);

    // Generate output based on method
    const output = generateOutput(processedItems, separator, method, options);

    // Calculate statistics
    const stats = computeStatistics(inputItems, processedItems, output);

    return {
      inputItems,
      finalItems: processedItems,
      separator,
      output,
      stats
    };
  };

  const preprocessItems = (items: string[], options: JoinSettings): string[] => {
    let processed = [...items];

    // Trim whitespace
    if (options.trimItems) {
      processed = processed.map(item => item.trim());
    }

    // Remove empty items
    if (options.removeEmpty) {
      processed = processed.filter(item => item !== '');
    }

    // Filter by length
    processed = processed.filter(item => 
      item.length >= options.minLength && item.length <= options.maxLength
    );

    // Remove duplicates
    if (options.removeDuplicates) {
      if (options.ignoreCase) {
        const seenLower = new Set<string>();
        processed = processed.filter(item => {
          const lowerItem = item.toLowerCase();
          if (seenLower.has(lowerItem)) {
            return false;
          }
          seenLower.add(lowerItem);
          return true;
        });
      } else {
        processed = Array.from(new Set(processed));
      }
    }

    // Sort items
    if (options.sortItems) {
      processed = processed.sort((a, b) => {
        const comparison = options.ignoreCase 
          ? a.toLowerCase().localeCompare(b.toLowerCase())
          : a.localeCompare(b);
        return options.reverseSort ? -comparison : comparison;
      });
    }

    // Limit count
    if (options.limitCount && options.maxCount > 0) {
      processed = processed.slice(0, options.maxCount);
    }

    return processed;
  };

  const generateOutput = (items: string[], separator: string, method: string, options: JoinSettings): string => {
    if (items.length === 0) return '';

    // Apply item formatting
    const formattedItems = items.map((item, index) => formatItem(item, index, items.length, options));

    switch (method) {
      case 'basic':
        return formattedItems.join(separator);

      case 'comma':
        return formattedItems.join(', ');

      case 'array':
        const arrayItems = formattedItems.map(item => `"${escapeString(item)}"`);
        return `[${arrayItems.join(', ')}]`;

      case 'bulletList':
        return formattedItems.map(item => `• ${item}`).join('\n');

      case 'numberedList':
        return formattedItems.map((item, index) => `${index + 1}. ${item}`).join('\n');

      case 'htmlList':
        const htmlItems = formattedItems.map(item => `  <li>${escapeHtml(item)}</li>`);
        return `<ul>\n${htmlItems.join('\n')}\n</ul>`;

      case 'markdownList':
        return formattedItems.map(item => `- ${item}`).join('\n');

      case 'sqlValues':
        const sqlItems = formattedItems.map(item => `('${escapeSql(item)}')`);
        return sqlItems.join(',\n');

      case 'jsonArray':
        return JSON.stringify(formattedItems, null, 2);

      case 'xmlList':
        const xmlItems = formattedItems.map(item => `  <item>${escapeXml(item)}</item>`);
        return `<list>\n${xmlItems.join('\n')}\n</list>`;

      case 'naturlaText':
        return createNaturalSentence(formattedItems);

      case 'customFormat':
        return createCustomFormat(formattedItems, separator, options);

      default:
        return formattedItems.join(separator);
    }
  };

  const formatItem = (item: string, index: number, total: number, options: JoinSettings): string => {
    let formatted = item;

    // Add prefix/suffix
    if (options.addPrefix) {
      formatted = options.addPrefix + formatted;
    }
    if (options.addSuffix) {
      formatted = formatted + options.addSuffix;
    }

    // Wrap items
    if (options.wrapItems) {
      formatted = options.wrapStart + formatted + options.wrapEnd;
    }

    // Add quotes
    if (options.addQuotes) {
      formatted = `"${formatted}"`;
    }

    // Escape special characters
    if (options.escapeChars) {
      formatted = escapeString(formatted);
    }

    // Add numbering
    if (options.addNumbering) {
      formatted = `${index + 1}. ${formatted}`;
    }

    return formatted;
  };

  const escapeString = (str: string): string => {
    return str
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/'/g, "\\'")
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r')
      .replace(/\t/g, '\\t');
  };

  const escapeHtml = (str: string): string => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const escapeSql = (str: string): string => {
    return str.replace(/'/g, "''");
  };

  const escapeXml = (str: string): string => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  const createNaturalSentence = (items: string[]): string => {
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    
    const lastItem = items[items.length - 1];
    const otherItems = items.slice(0, -1);
    return `${otherItems.join(', ')}, and ${lastItem}`;
  };

  const createCustomFormat = (items: string[], template: string, options: JoinSettings): string => {
    return items.map((item, index) => {
      return template
        .replace(/\{item\}/g, item)
        .replace(/\{index\}/g, (index + 1).toString())
        .replace(/\{total\}/g, items.length.toString())
        .replace(/\{index0\}/g, index.toString());
    }).join('\n');
  };

  const computeStatistics = (inputItems: string[], finalItems: string[], output: string): JoinStatistics => {
    const emptyRemoved = inputItems.filter(item => item.trim() === '').length;
    const duplicatesRemoved = inputItems.length - new Set(inputItems).size;
    
    const lengths = finalItems.map(item => item.length);
    const avgItemLength = lengths.length > 0 ? Math.round(lengths.reduce((sum, len) => sum + len, 0) / lengths.length) : 0;
    
    const longestItem = finalItems.reduce((longest, item) => item.length > longest.length ? item : longest, '');
    const shortestItem = finalItems.reduce((shortest, item) => item.length < shortest.length ? item : shortest, finalItems[0] || '');
    
    const uniqueCount = new Set(finalItems).size;

    return {
      originalCount: inputItems.length,
      finalCount: finalItems.length,
      emptyRemoved,
      duplicatesRemoved,
      outputLength: output.length,
      avgItemLength,
      longestItem,
      shortestItem,
      uniqueCount
    };
  };

  const joinResult: JoinResult = useMemo(() => {
    return executeJoin(inputText, joinSeparator, selectedJoinMethod, settings);
  }, [inputText, joinSeparator, selectedJoinMethod, settings]);

  useEffect(() => {
    setOutputText(joinResult.output);
  }, [joinResult]);

  const copyOutput = async () => {
    if (outputText) {
      await navigator.clipboard.writeText(outputText);
      setCopyMessage(true);
      setTimeout(() => setCopyMessage(false), 2000);
    }
  };

  const downloadOutput = () => {
    if (outputText) {
      const summary = `String Joiner Results:
Method: ${joinMethods.find(m => m.id === selectedJoinMethod)?.name}
Separator: "${joinSeparator}"
Original Items: ${joinResult.stats.originalCount}
Final Items: ${joinResult.stats.finalCount}
Output Length: ${joinResult.stats.outputLength}

Output:
${outputText}`;

      const blob = new Blob([summary], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'string-joiner-output.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const generateSampleItems = () => {
    const sampleSets = [
      'Apple\nBanana\nCherry\nDate\nElderry\nFig\nGrape\nHoneydew',
      'React\nVue\nAngular\nSvelte\nNext.js\nNuxt.js\nGatsby\nRemix',
      'Red\nGreen\nBlue\nYellow\nMagenta\nCyan\nOrange\nPurple\nPink',
      'Monday\nTuesday\nWednesday\nThursday\nFriday\nSaturday\nSunday',
      'JavaScript\nPython\nJava\nC#\nC++\nRust\nGo\nKotlin\nSwift',
      'New York\nLos Angeles\nChicago\nHouston\nPhoenix\nPhiladelphia\nSan Antonio',
      'CSS\nHTML\nJavaScript\nTypeScript\nSCSS\nLess\nTailwind\nBootstrap',
      'Alice\nBob\nCharlie\nDiana\nEve\nFrank\nGrace\nHenry\nIvy',
      'Task 1\nTask 2\nTask 3\nTask 4\nTask 5\nTask 6\nTask 7',
      'Winter\nSpring\nSummer\nFall\nAutumn'
    ];
    setInputText(sampleSets[Math.floor(Math.random() * sampleSets.length)]);
  };

  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      green: 'bg-green-50 border-green-200 text-green-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-800',
      pink: 'bg-pink-50 border-pink-200 text-pink-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      teal: 'bg-teal-50 border-teal-200 text-teal-800',
      cyan: 'bg-cyan-50 border-cyan-200 text-cyan-800',
      emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      violet: 'bg-violet-50 border-violet-200 text-violet-800'
    };
    return colorMap[color as keyof typeof colorMap] || colorMap.blue;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-100 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <Merge className="text-blue-600" />
            String Joiner Tool
          </h1>
          <p className="text-gray-600">Join and format text items with powerful customization options</p>
        </div>

        {/* Configuration Panel */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700">Join Configuration</h2>
            <div className="flex gap-2">
              <button
                onClick={generateSampleItems}
                className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
              >
                <Shuffle size={16} />
                Sample Items
              </button>
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
              >
                <Settings size={16} />
                Settings
              </button>
              <button
                onClick={() => setShowStatistics(!showStatistics)}
                className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm"
              >
                <Eye size={16} />
                Statistics
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-semibold text-gray-600 mb-2">
                {selectedJoinMethod === 'customFormat' ? 'Template' : 'Separator/Delimiter'}
              </label>
              <input
                type="text"
                value={joinSeparator}
                onChange={(e) => setJoinSeparator(e.target.value)}
                placeholder={
                  selectedJoinMethod === 'customFormat' ? 'Use {item}, {index}, {total}...' :
                  ['comma', 'array', 'bulletList', 'numberedList', 'htmlList', 'markdownList', 'sqlValues', 'jsonArray', 'xmlList', 'naturlaText'].includes(selectedJoinMethod) ?
                  'Not required for this method' :
                  'Enter separator...'
                }
                disabled={['comma', 'array', 'bulletList', 'numberedList', 'htmlList', 'markdownList', 'sqlValues', 'jsonArray', 'xmlList', 'naturlaText'].includes(selectedJoinMethod)}
                className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none disabled:bg-gray-100"
              />
            </div>
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                <p>Method: <span className="font-semibold">{joinMethods.find(m => m.id === selectedJoinMethod)?.name}</span></p>
                <p>Items: <span className="font-semibold">{joinResult.stats.finalCount}</span></p>
              </div>
            </div>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Settings className="text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-800">Join Settings</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Item Processing</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.trimItems}
                    onChange={(e) => setSettings({...settings, trimItems: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Trim Whitespace</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.removeEmpty}
                    onChange={(e) => setSettings({...settings, removeEmpty: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Remove Empty Items</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.removeDuplicates}
                    onChange={(e) => setSettings({...settings, removeDuplicates: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Remove Duplicates</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.ignoreCase}
                    onChange={(e) => setSettings({...settings, ignoreCase: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Ignore Case</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.sortItems}
                    onChange={(e) => setSettings({...settings, sortItems: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Sort Items</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.reverseSort}
                    onChange={(e) => setSettings({...settings, reverseSort: e.target.checked})}
                    disabled={!settings.sortItems}
                    className="w-4 h-4 text-purple-600 disabled:opacity-50"
                  />
                  <span className="text-sm">Reverse Sort</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Item Formatting</h3>
                
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.addQuotes}
                    onChange={(e) => setSettings({...settings, addQuotes: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Add Quotes</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.escapeChars}
                    onChange={(e) => setSettings({...settings, escapeChars: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Escape Special Characters</span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.addNumbering}
                    onChange={(e) => setSettings({...settings, addNumbering: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Add Numbering</span>
                </label>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Item Prefix</label>
                  <input
                    type="text"
                    value={settings.addPrefix}
                    onChange={(e) => setSettings({...settings, addPrefix: e.target.value})}
                    placeholder="Prefix for each item..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Item Suffix</label>
                  <input
                    type="text"
                    value={settings.addSuffix}
                    onChange={(e) => setSettings({...settings, addSuffix: e.target.value})}
                    placeholder="Suffix for each item..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.wrapItems}
                    onChange={(e) => setSettings({...settings, wrapItems: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Wrap Items</span>
                </label>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-700 border-b pb-2">Filters & Limits</h3>
                
                {settings.wrapItems && (
                  <>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Wrap Start</label>
                      <input
                        type="text"
                        value={settings.wrapStart}
                        onChange={(e) => setSettings({...settings, wrapStart: e.target.value})}
                        placeholder="Start wrapper..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Wrap End</label>
                      <input
                        type="text"
                        value={settings.wrapEnd}
                        onChange={(e) => setSettings({...settings, wrapEnd: e.target.value})}
                        placeholder="End wrapper..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Min Length</label>
                  <input
                    type="number"
                    min="0"
                    value={settings.minLength}
                    onChange={(e) => setSettings({...settings, minLength: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Max Length</label>
                  <input
                    type="number"
                    min="1"
                    value={settings.maxLength}
                    onChange={(e) => setSettings({...settings, maxLength: parseInt(e.target.value) || 1000})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                  />
                </div>

                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={settings.limitCount}
                    onChange={(e) => setSettings({...settings, limitCount: e.target.checked})}
                    className="w-4 h-4 text-purple-600"
                  />
                  <span className="text-sm">Limit Item Count</span>
                </label>

                {settings.limitCount && (
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Max Items</label>
                    <input
                      type="number"
                      min="1"
                      value={settings.maxCount}
                      onChange={(e) => setSettings({...settings, maxCount: parseInt(e.target.value) || 100})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-sm"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Input and Output */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Input Items</label>
                <div className="text-sm text-gray-500">
                  {inputText.split('\n').filter(line => line.trim()).length} items
                </div>
              </div>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="Enter items to join (one per line)..."
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none resize-none text-gray-700 transition-colors font-mono text-sm"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-lg font-semibold text-gray-700">Joined Output</label>
                <div className="flex items-center gap-2">
                  <div className="text-sm text-gray-500">
                    {outputText.length} characters
                  </div>
                  <button
                    onClick={copyOutput}
                    disabled={!outputText}
                    className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Copy size={16} />
                    {copyMessage ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadOutput}
                    disabled={!outputText}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              </div>
              <textarea
                value={outputText}
                readOnly
                className="w-full h-64 p-4 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700 resize-none font-mono text-sm"
                placeholder="Joined output will appear here..."
              />
            </div>
          </div>
        </div>

        {/* Join Methods */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Merge className="text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-800">Choose Join Method</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {joinMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => setSelectedJoinMethod(method.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  selectedJoinMethod === method.id
                    ? `border-${method.color}-500 ${getColorClasses(method.color)} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {method.icon}
                    <h3 className={`font-semibold ${
                      selectedJoinMethod === method.id ? '' : 'text-gray-800'
                    }`}>
                      {method.name}
                    </h3>
                  </div>
                  {selectedJoinMethod === method.id && (
                    <div className={`w-3 h-3 bg-${method.color}-500 rounded-full`}></div>
                  )}
                </div>
                <p className={`text-sm ${
                  selectedJoinMethod === method.id ? '' : 'text-gray-600'
                }`}>
                  {method.description}
                </p>
              </div>
            ))}
          </div>

          {/* Method Help */}
          <div className="mt-6 p-4 bg-gray-50 rounded-xl">
            <h3 className="font-semibold text-gray-700 mb-2">
              {joinMethods.find(m => m.id === selectedJoinMethod)?.name} Help
            </h3>
            <div className="text-sm text-gray-600">
              {selectedJoinMethod === 'basic' && (
                <div>
                  <p>• Simple joining with custom separator</p>
                  <p>• Example: "apple, banana, cherry" with ", " separator</p>
                </div>
              )}
              {selectedJoinMethod === 'comma' && (
                <div>
                  <p>• Standard comma-separated format</p>
                  <p>• Automatically uses ", " as separator</p>
                </div>
              )}
              {selectedJoinMethod === 'array' && (
                <div>
                  <p>• Programming array format: <code className="bg-gray-200 px-1 rounded">["item1", "item2"]</code></p>
                  <p>• Perfect for JavaScript, Python, and JSON</p>
                </div>
              )}
              {selectedJoinMethod === 'bulletList' && (
                <div>
                  <p>• Bulleted list with • symbols</p>
                  <p>• Each item on a new line with bullet point</p>
                </div>
              )}
              {selectedJoinMethod === 'numberedList' && (
                <div>
                  <p>• Sequential numbered list: 1. item1, 2. item2</p>
                  <p>• Automatic numbering for ordered lists</p>
                </div>
              )}
              {selectedJoinMethod === 'htmlList' && (
                <div>
                  <p>• HTML unordered list: <code className="bg-gray-200 px-1 rounded">&lt;ul&gt;&lt;li&gt;item&lt;/li&gt;&lt;/ul&gt;</code></p>
                  <p>• Ready for web development</p>
                </div>
              )}
              {selectedJoinMethod === 'markdownList' && (
                <div>
                  <p>• Markdown list format with - symbols</p>
                  <p>• Compatible with Markdown documents</p>
                </div>
              )}
              {selectedJoinMethod === 'sqlValues' && (
                <div>
                  <p>• SQL VALUES clause: <code className="bg-gray-200 px-1 rounded">('item1'), ('item2')</code></p>
                  <p>• Ready for database INSERT statements</p>
                </div>
              )}
              {selectedJoinMethod === 'jsonArray' && (
                <div>
                  <p>• Valid JSON array with proper formatting</p>
                  <p>• Includes proper indentation and escaping</p>
                </div>
              )}
              {selectedJoinMethod === 'xmlList' && (
                <div>
                  <p>• XML list structure with item tags</p>
                  <p>• Proper XML escaping included</p>
                </div>
              )}
              {selectedJoinMethod === 'naturlaText' && (
                <div>
                  <p>• Natural language format: "apple, banana, and cherry"</p>
                  <p>• Human-readable with "and" before last item</p>
                </div>
              )}
              {selectedJoinMethod === 'customFormat' && (
                <div>
                  <p>• Custom template with placeholders</p>
                  <p>• Use <code className="bg-gray-200 px-1 rounded">{`{item}`}</code>, <code className="bg-gray-200 px-1 rounded">{`{index}`}</code>, <code className="bg-gray-200 px-1 rounded">{`{total}`}</code>, <code className="bg-gray-200 px-1 rounded">{`{index0}`}</code></p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics */}
        {showStatistics && inputText && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Eye className="text-green-600" />
              <h2 className="text-xl font-semibold text-gray-800">Join Statistics</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <h3 className="font-semibold text-blue-800 mb-2">Original Items</h3>
                <div className="text-2xl font-bold text-blue-900">{joinResult.stats.originalCount}</div>
                <p className="text-sm text-blue-600">Input items</p>
              </div>

              <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Final Items</h3>
                <div className="text-2xl font-bold text-green-900">{joinResult.stats.finalCount}</div>
                <p className="text-sm text-green-600">After processing</p>
              </div>

              <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                <h3 className="font-semibold text-orange-800 mb-2">Output Length</h3>
                <div className="text-2xl font-bold text-orange-900">{joinResult.stats.outputLength}</div>
                <p className="text-sm text-orange-600">Total characters</p>
              </div>

              <div className="p-4 bg-purple-50 rounded-xl border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Avg Item Length</h3>
                <div className="text-2xl font-bold text-purple-900">{joinResult.stats.avgItemLength}</div>
                <p className="text-sm text-purple-600">Characters per item</p>
              </div>
            </div>

            {/* Additional Stats */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Unique Items</h4>
                <div className="text-lg font-bold text-gray-900">{joinResult.stats.uniqueCount}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Empty Removed</h4>
                <div className="text-lg font-bold text-gray-900">{joinResult.stats.emptyRemoved}</div>
              </div>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Duplicates Removed</h4>
                <div className="text-lg font-bold text-gray-900">{joinResult.stats.duplicatesRemoved}</div>
              </div>
            </div>

            {/* Item Analysis */}
            {joinResult.stats.longestItem && joinResult.stats.shortestItem && (
              <div className="mt-6">
                <h3 className="font-semibold text-gray-700 mb-3">Item Analysis:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <h4 className="font-semibold text-emerald-800 mb-1">
                      Longest Item ({joinResult.stats.longestItem.length} chars)
                    </h4>
                    <div className="text-sm text-emerald-700 font-mono truncate max-w-full">
                      {joinResult.stats.longestItem}
                    </div>
                  </div>
                  <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                    <h4 className="font-semibold text-teal-800 mb-1">
                      Shortest Item ({joinResult.stats.shortestItem.length} chars)
                    </h4>
                    <div className="text-sm text-teal-700 font-mono truncate max-w-full">
                      {joinResult.stats.shortestItem || '(empty)'}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Preview */}
        {joinResult.finalItems.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <List className="text-indigo-600" />
              <h2 className="text-xl font-semibold text-gray-800">Items Preview</h2>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto mb-6">
              {joinResult.finalItems.slice(0, 20).map((item, index) => (
                <div key={index} className="p-2 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 font-mono min-w-[3rem]">#{index + 1}</span>
                    <span className="text-sm font-mono text-gray-700 truncate max-w-md">
                      {item || '(empty)'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {item.length} chars
                  </div>
                </div>
              ))}
              {joinResult.finalItems.length > 20 && (
                <div className="text-sm text-gray-500 text-center py-2">
                  ...and {joinResult.finalItems.length - 20} more items
                </div>
              )}
            </div>

            {/* Output Preview */}
            <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
              <h3 className="font-semibold text-indigo-700 mb-2">Output Preview (first 1000 characters):</h3>
              <div className="text-sm font-mono text-indigo-800 bg-white p-3 rounded border max-h-40 overflow-auto whitespace-pre-wrap">
                {outputText.length > 1000 ? outputText.substring(0, 1000) + '...' : outputText}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StringJoinerTool;